<script>
import { mapGetters } from 'vuex';
import { hasPressedCommand } from 'shared/helpers/KeyboardHelpers';
import GalleryView from '../components/GalleryView.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { emitter } from 'shared/helpers/mitt';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert, useTrack } from 'dashboard/composables';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper.js';

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
};

export default {
  components: {
    Button,
    GalleryView,
  },
  props: {
    attachment: {
      type: Object,
      required: false,
    },
    message: {
      type: Object,
      required: true,
    },
  },
  emits: ['error'],
  data() {
    return {
      show: false,
      isImageError: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChatAttachments: 'getSelectedChatAttachments',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    isImage() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.IMAGE;
    },
    isVideo() {
      return (
        this.attachment.file_type === ALLOWED_FILE_TYPES.VIDEO ||
        this.attachment.file_type === ALLOWED_FILE_TYPES.IG_REEL
      );
    },
    isAudio() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.AUDIO;
    },
    timeStampURL() {
      return timeStampAppendedURL(this.dataUrl);
    },
    attachmentTypeClasses() {
      return {
        image: this.isImage,
        video: this.isVideo,
      };
    },
    filteredCurrentChatAttachments() {
      const attachments = this.currentChatAttachments.filter(attachment =>
        ['image', 'video', 'audio'].includes(attachment.file_type)
      );
      return attachments;
    },
    dataUrl() {
      return hasPermissions(['encrypt_message'], this.userPermissions)
        ? this.attachment.thumb_url
        : this.attachment.data_url;
    },
    imageWidth() {
      return this.attachment.width ? `${this.attachment.width}px` : 'auto';
    },
    imageHeight() {
      return this.attachment.height ? `${this.attachment.height}px` : 'auto';
    },
    isDisabledCreate() {
      return this.message.content_attributes?.order_info;
    },
    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },
    showDelete() {
      return !hasPermissions(['encrypt_message'], this.userPermissions);
    },
  },
  watch: {
    attachment() {
      this.isImageError = false;
    },
  },
  methods: {
    onClose() {
      this.show = false;
    },
    onClick(e) {
      if (hasPressedCommand(e)) {
        window.open(this.attachment.data_url, '_blank');
        return;
      }
      this.show = true;
    },
    onImgError() {
      this.isImageError = true;
      this.$emit('error');
    },
    onInitOrder() {
      emitter.emit('createGiftCardOrder', this.message);
    },
    notCard() {
      try {
        this.$store.dispatch('giftCards/notCard', {id: this.message.id});
        useAlert('Success');
      } catch (error) {
        useAlert(error);
      } finally {

      }
    },
  },
};
</script>

<template>
  <div class="message-text__wrap" :class="attachmentTypeClasses">
    <img
      v-if="isImage && !isImageError"
      class="bg-woot-200 dark:bg-woot-900 min-w-[300px] min-h-[300px] object-contain"
      :src="dataUrl"
      :width="imageWidth"
      :height="imageHeight"
      @click="onClick"
      @error="onImgError"
    />
  </div>

  <div class="flex flex-row bg-slate-200">
    <Button sm @click="onInitOrder" :disabled="isDisabledCreate">
      Create Order
    </Button>
    <Button sm ruby @click="notCard" v-if="showDelete" class="mx-3">
      Not Card
    </Button>
  </div>

  <GalleryView
    v-if="show && showDelete"
    v-model:show="show"
    :attachment="attachment"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onImgError"
    @close="onClose"
  />
</template>
